/* import __COLOCATED_TEMPLATE__ from './oauth-banner.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import type IntlService from 'embercom/services/intl';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';

const HIDE_IN_ROUTES = [
  'apps.app.loading',
  'apps.app.copilot-frame',
  'apps.app.settings.standalone.integration',
  'apps.app.standalone.deploy.zendesk.tickets.integrate',
  'apps.app.standalone.deploy.zendesk.messaging.integrate',
  'apps.app.standalone.deploy.intercom-messenger.integrate',
];

export interface Signature {
  Args: {
    onClick?: () => void;
    showInline?: boolean;
    /** When false, suppresses the Zendesk API OAuth and API key fallback banners. Default: true. */
    includeZendeskApi?: boolean;
    /** When false, suppresses the Sunshine API OAuth banner. Default: true. */
    includeSunshine?: boolean;
  };
  Blocks: {};
  Element: null;
}

export default class StandaloneZendeskOauthBanner extends Component<Signature> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare appService: { app: { hasStandalonePlatform: (type: string) => boolean } };

  /**
   * Returns the highest-priority connection that needs a reconnection banner,
   * respecting the includeZendeskApi / includeSunshine props.
   *
   * Priority: api-key-fallback → zendesk-api → sunshine-api
   */
  get priorityConnection(): 'api-key-fallback' | 'zendesk-api' | 'sunshine-api' | null {
    // Guard: finStandaloneService.zendeskConfig throws on non-Zendesk workspaces
    if (!this.appService.app.hasStandalonePlatform('zendesk')) {
      return null;
    }

    let includeZendesk = this.args.includeZendeskApi !== false;
    let includeSunshine = this.args.includeSunshine !== false;
    let zendeskConfig = this.finStandaloneService.zendeskConfig;

    if (includeZendesk && zendeskConfig?.shouldShowApiKeyFallbackBanner) {
      return 'api-key-fallback';
    }
    if (includeZendesk && zendeskConfig?.shouldShowZendeskApiOauthBanner) {
      return 'zendesk-api';
    }
    if (includeSunshine && zendeskConfig?.shouldShowSunshineApiOauthBanner) {
      return 'sunshine-api';
    }
    return null;
  }

  get isApiKeyFallbackBanner(): boolean {
    return this.priorityConnection === 'api-key-fallback';
  }

  get shouldShowBanner(): boolean {
    if (!this.args.showInline && HIDE_IN_ROUTES.includes(this.router.currentRouteName)) {
      return false;
    }
    return this.priorityConnection !== null;
  }

  get messageText(): string {
    if (this.isApiKeyFallbackBanner) {
      return this.args.showInline
        ? this.intl.t('standalone.zendesk.api-key-fallback-banner.message-inline')
        : this.intl.t('standalone.zendesk.api-key-fallback-banner.message');
    }
    if (this.priorityConnection === 'sunshine-api') {
      return this.args.showInline
        ? this.intl.t('standalone.zendesk.oauth-banner.sunshine-api.message-inline')
        : this.intl.t('standalone.zendesk.oauth-banner.sunshine-api.message');
    }
    return this.args.showInline
      ? this.intl.t('standalone.zendesk.oauth-banner.message-inline')
      : this.intl.t('standalone.zendesk.oauth-banner.message');
  }

  get buttonLabel(): string {
    if (this.isApiKeyFallbackBanner) {
      return this.intl.t('standalone.zendesk.api-key-fallback-banner.button-label');
    }
    if (this.priorityConnection === 'sunshine-api') {
      return this.intl.t('standalone.zendesk.oauth-banner.sunshine-api.button-label');
    }
    return this.intl.t('standalone.zendesk.oauth-banner.button-label');
  }

  @action
  buttonClick() {
    if (this.args.onClick) {
      this.args.onClick();
    } else {
      this.router.transitionTo('apps.app.settings.standalone.integration');
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Zendesk::OauthBanner': typeof StandaloneZendeskOauthBanner;
    'standalone/zendesk/oauth-banner': typeof StandaloneZendeskOauthBanner;
  }
}
